import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Grid3x3,
  Key,
  Users,
  Map,
  Ticket,
  Upload,
  Shield,
  FileText,
  LogOut,
  Lock,
  BarChart3,
  Key as KeyIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordModal from '../modals/ChangePasswordModal';

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { admin, logout } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const menuItems = [
    { path: '/admin/dashboard', label: t('admin.dashboard'), icon: LayoutDashboard },
    { path: '/admin/plans', label: t('admin.plans'), icon: Grid3x3 },
    { path: '/admin/api-keys', label: t('admin.apiKeys'), icon: Key },
    { path: '/admin/clients', label: t('admin.clients'), icon: Users },
    { path: '/admin/map', label: t('admin.map'), icon: Map },
    { path: '/admin/activation-codes', label: t('admin.activationCodes'), icon: Ticket },
    { path: '/admin/gravitee-import', label: t('admin.graviteeImport'), icon: Upload },
    { path: '/admin/admins', label: t('admin.admins'), icon: Shield },
    { path: '/admin/audit-logs', label: t('admin.auditLogs'), icon: FileText },
    { path: '/admin/model-accuracy', label: t('admin.modelAccuracy'), icon: BarChart3 },
    { path: '/admin/tokens', label: t('admin.tokens'), icon: KeyIcon },
    { path: '/admin/auth-headers', label: t('admin.authHeaders'), icon: KeyIcon },
  ];

  return (
    <div className="w-64 bg-gray-900 text-white min-h-screen flex flex-col">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-gray-800">
        <h1 className="text-xl font-bold">Solar Forecast API</h1>
        <p className="text-sm text-gray-400 mt-1">{t('admin.adminPanel')}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon className="h-5 w-5 mr-3" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User info and actions */}
      <div className="px-3 py-4 border-t border-gray-800">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-medium text-white">{admin?.name}</p>
          <p className="text-xs text-gray-400">{admin?.email}</p>
        </div>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="flex items-center w-full px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors mb-1"
        >
          <Lock className="h-5 w-5 mr-3" />
          <span>{t('admin.changePassword')}</span>
        </button>
        <button
          onClick={logout}
          className="flex items-center w-full px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        >
          <LogOut className="h-5 w-5 mr-3" />
          <span>{t('common.logout')}</span>
        </button>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
    </div>
  );
};

export default Sidebar;
